import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PrimaryButton } from '@/components/ui-kit/Buttons'
import { MascotBubble, MascotSlot } from '@/components/ui-kit/Mascot'
import { Headline } from '@/components/ui-kit/Primitives'
import { ScreenShell, TopBar } from '@/components/ui-kit/Shell'
import { CONFIDENCE_MAX, CONFIDENCE_MIN, GUESS_MAX, GUESS_MIN, clamp } from '@/lib/game'
import { cn } from '@/lib/utils'

interface AssessScreenProps {
    onSubmit: (guess: number, confidence: number) => void
    onHelp?: () => void
}

/**
 * Die Selbsteinschätzung vor der Auflösung.
 *
 * Der Wert lässt sich per Regler UND direkt als Zahl eingeben: Der Regler ist
 * schnell, die Tastatur genau. Beide Wege werden auf −100…+200 begrenzt, damit
 * eine vertippte Zahl nicht die Auswertung der Klasse verzerrt.
 */
export function AssessScreen({ onSubmit, onHelp }: AssessScreenProps) {
    const { t } = useTranslation()
    // WETTBANDIT-66: Nichts ist vorbelegt. Vorher standen 0 EUR und "Mittel"
    // schon da und wurden uebersehen; weiter geht es erst mit eigener Eingabe.
    const [guess, setGuess] = useState<number | null>(null)
    // Getrennt vom Zahlwert: waehrend des Tippens darf "-" oder "" im Feld
    // stehen, ohne dass daraus sofort eine 0 wird.
    const [guessText, setGuessText] = useState('')
    const [confidence, setConfidence] = useState<number | null>(null)

    const applyGuess = (raw: string) => {
        setGuessText(raw)

        const parsed = Number.parseInt(raw, 10)

        if (!Number.isNaN(parsed)) {
            setGuess(clamp(parsed, GUESS_MIN, GUESS_MAX))
        }
    }

    const commitGuess = () => {
        const parsed = Number.parseInt(guessText, 10)

        if (Number.isNaN(parsed)) {
            setGuess(null)
            setGuessText('')

            return
        }

        const next = clamp(parsed, GUESS_MIN, GUESS_MAX)

        setGuess(next)
        setGuessText(String(next))
    }

    const setFromSlider = (value: number) => {
        setGuess(value)
        setGuessText(String(value))
    }

    const confidenceLabels = t('assess.confidenceLabels', {
        returnObjects: true,
    }) as string[]

    return (
        <ScreenShell>
            <TopBar title={t('assess.title')} onHelp={onHelp} />

            {/* Innenabstand fuer den Ring und den Schatten der Sprechblase: overflow-y-auto
                schneidet alles ab, was ueber die Box hinausragt - seit die Blase bis an
                den Rand reicht (WETTBANDIT-44), traf es rechts den Ring und unten den
                Schatten. -mx-1 haelt die Inhalte trotzdem auf ihrer Linie. */}
            <div className="-mx-1 wb-scroll flex flex-1 flex-col gap-4 overflow-y-auto px-1 pt-3 pb-8">
                <div className="flex items-center gap-2.5">
                    <MascotSlot pose="lecturing" size={88} />
                    <MascotBubble>{t('assess.bubble')}</MascotBubble>
                </div>

                <div>
                    <Headline className="text-xl">{t('assess.headline')}</Headline>
                    <p className="mt-1.5 text-sm text-wb-dim">{t('assess.body')}</p>
                </div>

                <section className="flex flex-col gap-3 rounded-2xl border border-wb-border bg-wb-panel p-4">
                    <label className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('assess.guess')}
                    </label>

                    <div className="flex items-baseline gap-2">
                        <input
                            type="number"
                            inputMode="numeric"
                            value={guessText}
                            placeholder="–"
                            min={GUESS_MIN}
                            max={GUESS_MAX}
                            onChange={(event) => applyGuess(event.target.value)}
                            onBlur={commitGuess}
                            aria-label={t('assess.guess')}
                            className="w-36 rounded-xl border-2 border-wb-border bg-wb-panel-lo px-3 py-2 wb-num text-3xl font-extrabold text-wb-accent outline-none focus:border-wb-accent"
                        />
                        <span className="wb-num text-xl text-wb-muted">€</span>
                    </div>

                    <input
                        type="range"
                        min={GUESS_MIN}
                        max={GUESS_MAX}
                        step={5}
                        value={guess ?? 0}
                        onChange={(event) => setFromSlider(Number(event.target.value))}
                        onPointerUp={(event) => {
                            if (guess === null) {
                                setFromSlider(Number(event.currentTarget.value))
                            }
                        }}
                        aria-label={t('assess.guess')}
                        className={cn(
                            'w-full',
                            guess === null ? 'accent-wb-muted opacity-60' : 'accent-wb-accent',
                        )}
                    />
                    <div className="flex justify-between text-[11px] text-wb-muted">
                        <span>{GUESS_MIN} €</span>
                        <span>+{GUESS_MAX} €</span>
                    </div>
                </section>

                <section className="flex flex-col gap-3 rounded-2xl border border-wb-border bg-wb-panel p-4">
                    <label className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('assess.confidence')}
                    </label>

                    <div
                        className={cn(
                            'text-base font-bold',
                            confidence === null && 'text-wb-muted',
                        )}
                        data-testid="confidence-label"
                    >
                        {confidence === null
                            ? t('assess.confidenceEmpty')
                            : confidenceLabels[confidence - 1]}
                    </div>

                    <input
                        type="range"
                        min={CONFIDENCE_MIN}
                        max={CONFIDENCE_MAX}
                        step={1}
                        value={confidence ?? Math.round((CONFIDENCE_MIN + CONFIDENCE_MAX) / 2)}
                        onChange={(event) => setConfidence(Number(event.target.value))}
                        // Wer genau den angezeigten Mittelwert antippt, loest kein
                        // change-Event aus - die Antwort bliebe sonst leer.
                        onPointerUp={(event) => {
                            const value = Number(event.currentTarget.value)

                            setConfidence((current) => current ?? value)
                        }}
                        aria-label={t('assess.confidence')}
                        className={cn(
                            'w-full',
                            confidence === null ? 'accent-wb-muted opacity-60' : 'accent-wb-accent',
                        )}
                    />

                    <div className="flex gap-1" aria-hidden="true">
                        {Array.from({ length: CONFIDENCE_MAX }).map((_, index) => (
                            <span
                                key={index}
                                className={cn(
                                    'h-1 flex-1 rounded-full',
                                    index < (confidence ?? 0) ? 'bg-wb-accent' : 'bg-wb-border',
                                )}
                            />
                        ))}
                    </div>
                </section>
            </div>

            <PrimaryButton
                className="mt-3"
                disabled={guess === null || confidence === null}
                onClick={() => guess !== null && confidence !== null && onSubmit(guess, confidence)}
            >
                {t('assess.submit')}
            </PrimaryButton>
        </ScreenShell>
    )
}
